import React from 'react';
import MediaQueries from 'react-responsive';

import Heading from '@marvelapp/marvel-ui/heading';
import Paragraph from '@marvelapp/marvel-ui/paragraph';
import Spacing from '@marvelapp/marvel-ui/spacing';
import Align from '@marvelapp/marvel-ui/align';
import Link from '@marvelapp/marvel-ui/link';

import Page from 'marketing-website/components/modules/Page';
import SecurityShield from 'marketing-website/components/icons/SecurityShield';
import metaData from 'marketing-website/data/metadata.js';

import Stats from './Stats';
import Features from './Features';
import Actions from './Actions';

import styles from './style/general.module.css';

export default function Security() {
  return (
    <Page
      title={metaData.security.title}
      meta={[
        {
          name: 'description',
          content: metaData.security.description,
        },
      ]}
      banner
      header
      footer
    >
      <div className={styles.security}>
        <div className={styles.wrapper}>
          <div className={styles.leftcol}>
            <MediaQueries minWidth={770}>
              <div>
                <Spacing type="margin" size="l" position="bottom">
                  <Align type="center">
                    <SecurityShield />
                  </Align>
                </Spacing>
                <Spacing type="margin" size="m" position="bottom">
                  <Actions />
                  <Spacing type="margin" size="m" position="bottom">
                    <Heading tag="h3" weight="bold" size="m">
                      Security features
                    </Heading>
                  </Spacing>
                  <Features size="m" />
                </Spacing>
              </div>
            </MediaQueries>
          </div>
          <div className={styles.rightcol}>
            <Spacing type="margin" size="s" sizeM="l" position="bottom">
              <Heading tag="h1" weight="bold" size="xxxxl">
                Security at Marvel
              </Heading>
            </Spacing>
            <Spacing type="margin" size="l" position="bottom">
              <Paragraph size="xxxl">
                We're committed to protecting the creative work that our
                customers trust us to store.
              </Paragraph>
            </Spacing>

            <MediaQueries maxWidth={769}>
              <Spacing type="margin" size="l" position="bottom">
                <Actions />
                <Spacing type="margin" size="m" position="bottom">
                  <Heading tag="h3" weight="bold" size="xxl">
                    Security features
                  </Heading>
                </Spacing>
                <Features size="l" />
              </Spacing>
            </MediaQueries>
            <Spacing type="margin" size="l" position="bottom">
              <Spacing type="margin" size="m" position="bottom">
                <Paragraph size="l" color="steel">
                  Marvel’s mission is to democratise the design process, making
                  it more productive and inclusive so that everyone has the
                  opportunity to bring their ideas to life. Core to that mission
                  is keeping your ideas, data and projects confidential and
                  secure. We take that responsibility seriously for our 2
                  million users, ranging from startups to Fortune 100 companies.
                </Paragraph>
              </Spacing>
              <Paragraph size="l" color="steel">
                Here you’ll find more information on how we approach security,
                if you have additional questions feel free to get in touch at on{' '}
                <Link url="mailto:security@marvelapp.com" type="blue">
                  security@marvelapp.com
                </Link>
              </Paragraph>
            </Spacing>
            <Spacing type="margin" size="m" position="bottom">
              <Heading tag="h3" weight="bold" size="xxl">
                Vulnerability disclosure and Reward Program
              </Heading>
            </Spacing>
            <Spacing type="margin" size="l" position="bottom">
              <Paragraph size="l" color="steel">
                Marvel’s security team works closely with the community to
                rapidly investigate all reported issues regarding security and
                bugs on the platform. We value the effort and detail put into
                reports we receive by the security research community and offer
                cash rewards based on the severity of issue reported. If you
                find any security issues please review our{' '}
                <Link url="/security/bountypolicy/" type="blue">
                  Bug Bounty Policy
                </Link>{' '}
                before submitting any requests. Any submissions that do not
                qualify may not be responded to as it requires time for our
                engineers to review each submissions please ensure you read all
                scopes for our submission criteria. We endeavour to reply to any
                real or high impact issues within 7 days of submission with a
                maximum of 30 days depending on the problem.
              </Paragraph>
            </Spacing>
            <Spacing type="margin" size="m" position="bottom">
              <Heading tag="h3" weight="bold" size="xxl">
                Enterprise Security Features
              </Heading>
            </Spacing>
            <Spacing type="margin" size="l" position="bottom">
              <Paragraph size="l" color="steel">
                Marvel Enterprise has several additional features that allow
                customers to enforce several layers of security to their
                accounts and projects. That includes Single Sign-On (SSO), IP
                restriction, access logs, user permissions and project-level
                password protection.
              </Paragraph>
            </Spacing>
            <Spacing type="margin" size="m" position="bottom">
              <Heading tag="h3" weight="bold" size="xxl">
                Communication
              </Heading>
            </Spacing>
            <Spacing type="margin" size="l" position="bottom">
              <Paragraph size="l" color="steel">
                All user data is transported securely, as all traffic is
                encrypted in transit via SSL. Encrypting data in transit
                protects it from unauthorised snooping, modification, and
                man-in-the-middle attacks. We use 256-bit SSL/TLS.1.2
                encryption, utilising both the ECDSA and RSA algorithms.
              </Paragraph>
            </Spacing>
            <Spacing type="margin" size="m" position="bottom">
              <Heading tag="h3" weight="bold" size="xxl">
                Data Centre Security
              </Heading>
            </Spacing>
            <Paragraph size="l" color="steel">
              Our hosting environment is fully-redundant with disaster recovery
              procedures. Our cloud hosting providers maintain multiple
              certifications for its data centers, including ISO 27001
              compliance, PCI certification, and SOC. For more information about
              their certification and compliance, please visit the{' '}
              <Link
                url="https://cloud.google.com/security/compliance/#/"
                type="blue"
              >
                Google Cloud Platform
              </Link>{' '}
              or{' '}
              <Link url="https://aws.amazon.com/security/" type="blue">
                Amazon AWS security
              </Link>{' '}
              site.
            </Paragraph>
          </div>
        </div>
        <Stats />
        <div className={styles.wrapper}>
          <div className={styles.leftcol} />
          <div className={styles.rightcol}>
            <Spacing type="margin" size="m" position="bottom">
              <Heading tag="h3" weight="bold" size="xxl">
                EU Hosted Infrastructure
              </Heading>
            </Spacing>
            <Spacing type="margin" size="l" position="bottom">
              <Paragraph size="l" color="steel">
                The Marvel Platform infrastructure is hosted on servers based in
                the European Union. This allows us to meet specific regulatory
                and compliance requirements of organisations in Europe,
                including financial institutions, consultancies and government
                entities. Our data center provider Google Cloud Platform located
                in Belgium and Germany maintains multiple certifications,
                including SOC 1, SOC 2, SOC 3 and ISO27001. In addition all data
                is encrypted both in transit and at rest using strong
                encryption.
              </Paragraph>
            </Spacing>

            <Spacing type="margin" size="m" position="bottom">
              <Heading tag="h3" weight="bold" size="xxl">
                Credit Cards
              </Heading>
            </Spacing>
            <Spacing type="margin" size="l" position="bottom">
              <Paragraph size="l" color="steel">
                Marvel does not store any credit card information. We have
                partnered with Stripe for credit card processing which allows us
                to leverage AES-256 encryption at rest, with PCI Service
                Provider Level 1 standards in the storage and handling of credit
                card information. This is the most stringent level of
                certification available to the payments industry.
              </Paragraph>
            </Spacing>
            <Spacing type="margin" size="m" position="bottom">
              <Heading tag="h3" weight="bold" size="xxl">
                Marvel’s GDPR Commitment
              </Heading>
            </Spacing>
            <Spacing type="margin" size="xl" position="bottom">
              <Paragraph size="l" color="steel">
                As a Data Controller, Marvel is commited to compliance with the
                General Data Protection Regulation, and meeting our legal
                obligation by helping our customers become compliant. We’ve made
                several key policy, product and term updates that strengthen the
                protection of personal data and rights of users.
              </Paragraph>
            </Spacing>
          </div>
        </div>
      </div>
    </Page>
  );
}



// WEBPACK FOOTER //
// ./src/pages/security/index.js